import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Plus } from 'lucide-react';
import { useTaskStore } from '@/app/store/taskStore';
import { useAuthStore } from '@/app/store/authStore';
import { taskService } from '@/app/services/taskService';
import { listUsers } from '@/app/services/userManagementService';
import { TaskStatus, TaskPriority } from '@/app/types/task';

// Minimal schema for quick todo - just title and assignee
const quickTodoSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  assigned_to_user_id: z.number().optional(),
});

type QuickTodoFormValues = z.infer<typeof quickTodoSchema>;

export const QuickTodoModal: React.FC = () => {
  const queryClient = useQueryClient();
  const { isQuickTodoModalOpen, closeQuickTodoModal } = useTaskStore();
  const { user } = useAuthStore();

  // Get current user's ID as number
  const currentUserId = user?.id ? parseInt(user.id, 10) : undefined;

  const form = useForm<QuickTodoFormValues>({
    resolver: zodResolver(quickTodoSchema),
    defaultValues: {
      title: '',
      assigned_to_user_id: currentUserId,
    },
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isQuickTodoModalOpen) {
      form.reset({
        title: '',
        assigned_to_user_id: currentUserId,
      });
    }
  }, [isQuickTodoModalOpen, currentUserId, form]);

  // Fetch users for assignment dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: () => listUsers({ limit: 100, skip: 0 }),
    enabled: isQuickTodoModalOpen,
  });

  // Create task mutation
  const createMutation = useMutation({
    mutationFn: taskService.create,
    onSuccess: (task) => {
      // Invalidate task-related queries
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskCounts'] });
      queryClient.invalidateQueries({ queryKey: ['open-tasks-count'] });
      queryClient.invalidateQueries({ queryKey: ['my-tasks'] });

      toast.success('Todo created!', {
        description: task.title,
        action: {
          label: 'View Tasks',
          onClick: () => window.location.href = '/tasks',
        },
      });

      closeQuickTodoModal();
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      toast.error('Failed to create todo', {
        description: error?.response?.data?.detail || 'Please try again',
      });
    },
  });

  const onSubmit = (values: QuickTodoFormValues) => {
    // Create task data - parent_entity fields are required by type but backend accepts empty
    const taskData = {
      title: values.title,
      assigned_to_user_id: values.assigned_to_user_id,
      status: TaskStatus.OPEN,
      priority: TaskPriority.MEDIUM,
      parent_entity_type: '', // Empty string for no parent
      parent_entity_id: 0, // 0 for no parent
      task_type: 'todo', // Mark as todo so it appears in To-Do list
    };

    createMutation.mutate(taskData);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !createMutation.isPending) {
      e.preventDefault();
      form.handleSubmit(onSubmit)();
    }
  };

  return (
    <Dialog open={isQuickTodoModalOpen} onOpenChange={(open) => !open && closeQuickTodoModal()}>
      <DialogContent className="sm:max-w-[425px]" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            Quick Add Todo
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What needs to be done?</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g., Review client documents"
                      autoFocus
                      className="text-base"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Assign To */}
            <FormField
              control={form.control}
              name="assigned_to_user_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assign to</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === 'unassigned' ? undefined : parseInt(value))
                    }
                    value={field.value?.toString() || 'unassigned'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {usersData?.items?.map((u) => (
                        <SelectItem key={u.id} value={u.id.toString()}>
                          {u.full_name || u.username}
                          {Number(u.id) === currentUserId && ' (Me)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeQuickTodoModal}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                className="bg-primary hover:bg-primary/90"
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Todo
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
